// Components/Footer/Footer.js
import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa";
import {
  FiMail,
  FiMapPin,
  FiPhone,
  FiShield,
} from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const WIDGET_ID = "6a033f5f35331a600d031cca";

function removeWidget() {
  const script = document.querySelector(
    `script[data-widget-id="${WIDGET_ID}"]`
  );
  if (script) script.remove();

  const selectors = [
    "#ghl-chat-widget",
    "chat-widget-launcher",
    "lea-chat-widget",
    `[data-widget-id="${WIDGET_ID}"]`,
  ];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.remove());
  });
}

function loadWidget() {
  if (document.querySelector(`script[data-widget-id="${WIDGET_ID}"]`)) return;

  const ghlScript = document.createElement("script");
  ghlScript.src = "https://widgets.leadconnectorhq.com/loader.js";
  ghlScript.setAttribute(
    "data-resources-url",
    "https://widgets.leadconnectorhq.com/chat-widget/loader.js"
  );
  ghlScript.setAttribute("data-widget-id", WIDGET_ID);
  ghlScript.setAttribute("data-source", "WEB_USER");
  ghlScript.async = true;
  document.body.appendChild(ghlScript);
}

export default function Footer() {
  const location = useLocation();

  useEffect(() => {
    const isContact = location.pathname === "/contact";

    if (isContact) {
      // Remove immediately
      removeWidget();

      // Keep removing every 100ms for 3 seconds to catch async widget injection
      const intervalId = setInterval(removeWidget, 100);
      const timeoutId = setTimeout(() => clearInterval(intervalId), 3000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    } else {
      // Small delay so DOM is ready, then load
      const timeoutId = setTimeout(loadWidget, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname]);

  // ← Extra safety: run synchronously on every render when on /contact
  if (location.pathname === "/contact") {
    removeWidget();
  }

  return (
    <footer className="bg-[#f8f9fa] text-[#495057] py-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">

        {/* Columns */}
        <motion.div
          className="flex flex-col lg:flex-row lg:items-start gap-10 mb-10"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >

          {/* Column 1 */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col lg:w-[28%] shrink-0"
          >
            <Link to="/" className="flex items-center mb-4">
              <img
                src="/Images/logo.png"
                alt="Ashlar Building Group Logo"
                className="h-20 sm:h-24 w-auto"
              />
            </Link>

            <p className="text-[#6c757d] text-sm mt-2 mb-5">
              Ashlar Building Group is a third-generation general
              contracting firm specializing in remodels, ADUs,
              and custom construction.
            </p>

            <div className="flex items-center gap-3">
              {[
                {
                  href: "https://www.facebook.com/ashlarbuilding",
                  icon: <FaFacebookF className="w-3.5 h-3.5" />,
                  label: "Facebook",
                  baseBg: "bg-[#e7f0fd]",
                  baseColor: "text-[#1877F2]",
                  hoverBg: "hover:bg-[#1877F2]",
                },
                {
                  href: "https://www.instagram.com/ashlarbuilding/?hl=en",
                  icon: <FaInstagram className="w-3.5 h-3.5" />,
                  label: "Instagram",
                  baseBg: "bg-[#fce4f0]",
                  baseColor: "text-[#ee2a7b]",
                  hoverBg: "hover:bg-[#ee2a7b]",
                },
                {
                  href: "https://www.linkedin.com/company/ashlar-building",
                  icon: <FaLinkedinIn className="w-3.5 h-3.5" />,
                  label: "LinkedIn",
                  baseBg: "bg-[#e3edf8]",
                  baseColor: "text-[#0A66C2]",
                  hoverBg: "hover:bg-[#0A66C2]",
                },
              ].map((s, i) => (
                <motion.a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  whileHover={{ scale: 1.15, y: -2 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 18,
                  }}
                  className={`w-8 h-8 rounded-full ${s.baseBg} ${s.baseColor} ${s.hoverBg} hover:text-white flex items-center justify-center transition-all duration-300`}
                >
                  {s.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Column 2 */}
          <motion.div
            variants={fadeUp}
            className="lg:w-[20%] shrink-0"
          >
            <h4 className="text-[#212529] font-bold mb-4">
              Quick Links
            </h4>

            <ul className="text-[#6c757d] text-sm space-y-2">
              {[
                { label: "About Us", to: "/about" },
                { label: "Our Process", to: "/process" },
                { label: "Portfolio", to: "/portfolio" },
                { label: "Contact Us", to: "/contact" },
                { label: "Privacy Policy", to: "/privacy-policy" },
                { label: "Terms of Service", to: "/terms-of-service" },
              ].map((link, i) => (
                <motion.li
                  key={i}
                  whileHover={{ x: 4 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                  }}
                >
                  <Link
                    to={link.to}
                    className="hover:text-[#343a40] transition-colors"
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Column 3 */}
          <motion.div
            variants={fadeUp}
            className="lg:w-[20%] shrink-0"
          >
            <h4 className="text-[#212529] font-bold mb-4">
              Services
            </h4>

            <ul className="text-[#6c757d] text-sm space-y-2">
              {[
                {
                  label: "ADUs & Custom Homes",
                  to: "/services/custom-homes",
                },
                {
                  label: "Full Home Remodels",
                  to: "/services/full-home-remodels",
                },
                {
                  label: "HOA & Commercial",
                  to: "/services/hoa-commercial",
                },
                {
                  label: "Takeover Projects",
                  to: "/services/takeover-projects",
                },
                {
                  label: "All Services",
                  to: "/services",
                },
              ].map((link, i) => (
                <motion.li
                  key={i}
                  whileHover={{ x: 4 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                  }}
                >
                  <Link
                    to={link.to}
                    className="hover:text-[#343a40] transition-colors"
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Column 4 */}
        {/* Column 4 */}
<motion.div
  variants={fadeUp}
  className="lg:w-[28%] shrink-0"
>
  <h4 className="text-[#212529] font-bold mb-4">
    Contact
  </h4>

  <ul className="text-[#6c757d] text-sm space-y-3">

    <li className="flex items-start gap-2">
      <FiMail className="w-4 h-4 flex-shrink-0 text-[#495057] mt-0.5" />
      <a
        href="mailto:info@ashlarbuildinggroup.com"
        className="hover:text-[#343a40] transition-colors break-all"
      >
        info@ashlarbuildinggroup.com
      </a>
    </li>

    <li className="flex items-start gap-2">
      <FiMapPin className="w-4 h-4 flex-shrink-0 text-[#495057] mt-0.5" />
      <span>6200 Stoneridge Mall Rd, Pleasanton, CA</span>
    </li>

    <li className="flex items-center gap-2">
      <FiPhone className="w-4 h-4 flex-shrink-0 text-[#495057]" />
      <a
        href="tel:+19256557741"
        className="hover:text-[#343a40] transition-colors"
      >
        (925) 655-7741
      </a>
    </li>

    <li className="flex items-center gap-2">
      <FiShield className="w-4 h-4 flex-shrink-0 text-[#495057]" />
      <span>License #1130737</span>
    </li>

  </ul>
</motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-[#dee2e6] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#adb5bd]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.8,
            delay: 0.3,
          }}
        >
          <p>
            &copy; 2026 Ashlar Building Group LLC. All rights reserved.
          </p>

          <div className="flex items-center gap-4">
            <Link
              to="/privacy-policy"
              className="hover:text-[#6c757d] transition-colors"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-of-service"
              className="hover:text-[#6c757d] transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </motion.div>

      </div>
    </footer>
  );
}